'use strict';
import React, { useState, useEffect, useContext } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import PageShell from '../components/PageShell';
import api from '../services/api';
import { LangContext } from '../App';
import { useAuth } from '../contexts/AuthContext';

// ── Design system ─────────────────────────────────────────────────────────────

const OR = 'var(--couleur-or)';
const TEXTE = 'var(--couleur-texte)';
const TEXTE_DIM = 'rgba(255,255,255,0.55)';
const BORDURE = 'rgba(255,255,255,0.14)';

// ── Translations ──────────────────────────────────────────────────────────────

const T = {
  fr: {
    retour: '← Retour aux terres',
    retourMesTerres: '← Mes terres',
    modifier: '✏️ Modifier',
    chargement: 'Chargement…',
    erreur: 'Impossible de charger cette terre.',
    introuvable: 'Terre introuvable.',

    sectionInfos: 'Informations',
    superficie: 'Superficie',
    capaciteMax: 'Capacité maximum',
    chasseurs: (n) => `${n} chasseur${n > 1 ? 's' : ''}`,
    zone: 'Zone',
    address: 'Adresse',
    especes: 'Espèces disponibles',
    rules: 'Règles & restrictions',
    aucuneRegle: 'Aucune règle spécifique.',
    proprietaire: 'Propriétaire',

    sectionPlan: 'Plan du territoire',
    sectionOffres: 'Offres actives',
    aucuneOffre: 'Aucune offre active sur cette terre',
    aucuneOffreDetail:
      "Le propriétaire n'a pas encore publié d'encan ni de réservation à prix fixe pour cette terre.",
    voirToutes: 'Voir les autres terres disponibles',

    offreEnchere: 'Encan',
    offreFixe: 'Prix fixe',
    prixPlancher: 'Mise plancher',
    prixFixeLabel: 'Prix par chasseur',
    places: 'places',
    fermetureLe: 'Ferme le',
    debutLe: 'Début',
    finLe: 'Fin',
    placerMise: 'Placer une mise →',
    reserver: 'Réserver →',
    voirDetail: 'Voir le détail →',

    statutBrouillon: 'Brouillon',
    statutEnCours: 'En cours',
    statutTerminee: 'Terminé',
    statutAnnulee: 'Annulé',
  },
  en: {
    retour: '← Back to lands',
    retourMesTerres: '← My lands',
    modifier: '✏️ Edit',
    chargement: 'Loading…',
    erreur: 'Could not load this land.',
    introuvable: 'Land not found.',

    sectionInfos: 'Information',
    superficie: 'Area',
    capaciteMax: 'Maximum capacity',
    chasseurs: (n) => `${n} hunter${n > 1 ? 's' : ''}`,
    zone: 'Zone',
    address: 'Address',
    especes: 'Available species',
    rules: 'Rules & restrictions',
    aucuneRegle: 'No specific rules.',
    proprietaire: 'Owner',

    sectionPlan: 'Territory map',
    sectionOffres: 'Active offers',
    aucuneOffre: 'No active offers on this land',
    aucuneOffreDetail:
      'The owner has not yet published an auction or fixed-price reservation for this land.',
    voirToutes: 'Browse other available lands',

    offreEnchere: 'Auction',
    offreFixe: 'Fixed price',
    prixPlancher: 'Floor bid',
    prixFixeLabel: 'Price per hunter',
    places: 'seats',
    fermetureLe: 'Closes',
    debutLe: 'Start',
    finLe: 'End',
    placerMise: 'Place a bid →',
    reserver: 'Reserve →',
    voirDetail: 'View details →',

    statutBrouillon: 'Draft',
    statutEnCours: 'Live',
    statutTerminee: 'Ended',
    statutAnnulee: 'Cancelled',
  },
};

function formatDate(d, lang) {
  if (!d) return '—';
  const date = new Date(d);
  return date.toLocaleDateString(lang === 'fr' ? 'fr-CA' : 'en-CA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

// ── Sub-component: a single listing card ──────────────────────────────────────

function CarteOffre({ listing, t, lang }) {
  const estEnchere = listing.mode === 'enchere';
  const price = estEnchere ? listing.floor_price : listing.fixed_price;
  const ctaLabel = estEnchere ? t.placerMise : t.reserver;

  return (
    <Link
      to={`/auctions/${listing.id}`}
      style={{
        display: 'block',
        textDecoration: 'none',
        color: 'inherit',
        background: 'var(--couleur-fond-carte)',
        border: `1px solid ${estEnchere ? OR + '55' : 'rgba(34,197,94,0.4)'}`,
        borderRadius: 10,
        padding: '1.4rem',
        transition: 'transform 0.15s, box-shadow 0.15s',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.35)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: '1rem',
          marginBottom: '0.8rem',
        }}
      >
        <span
          style={{
            background: estEnchere ? OR : 'rgba(34,197,94,0.85)',
            color: estEnchere ? '#1c2e1e' : '#062b1f',
            fontSize: '0.72rem',
            fontWeight: 700,
            padding: '4px 10px',
            borderRadius: 12,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
          }}
        >
          {estEnchere ? t.offreEnchere : t.offreFixe}
        </span>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '0.72rem', color: TEXTE_DIM, marginBottom: 2 }}>
            {estEnchere ? t.prixPlancher : t.prixFixeLabel}
          </div>
          <div style={{ color: OR, fontWeight: 700, fontSize: '1.4rem' }}>
            {price ? `${Number(price).toFixed(0)} $` : '—'}
          </div>
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '0.7rem',
          marginBottom: '1rem',
          fontSize: '0.85rem',
        }}
      >
        {listing.start_date && (
          <div>
            <div style={{ fontSize: '0.7rem', color: TEXTE_DIM, marginBottom: 2 }}>{t.debutLe}</div>
            <div style={{ color: TEXTE }}>{formatDate(listing.start_date, lang)}</div>
          </div>
        )}
        {listing.end_date && (
          <div>
            <div style={{ fontSize: '0.7rem', color: TEXTE_DIM, marginBottom: 2 }}>
              {estEnchere && listing.auction_closes_at ? t.fermetureLe : t.finLe}
            </div>
            <div style={{ color: TEXTE }}>
              {formatDate(
                estEnchere && listing.auction_closes_at
                  ? listing.auction_closes_at
                  : listing.end_date,
                lang
              )}
            </div>
          </div>
        )}
        {listing.max_seats != null && (
          <div>
            <div style={{ fontSize: '0.7rem', color: TEXTE_DIM, marginBottom: 2 }}>{t.places}</div>
            <div style={{ color: TEXTE }}>{listing.max_seats}</div>
          </div>
        )}
      </div>

      <div
        style={{
          color: OR,
          fontWeight: 600,
          fontSize: '0.9rem',
          textAlign: 'right',
        }}
      >
        {ctaLabel}
      </div>
    </Link>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function LandDetail() {
  const { id } = useParams();
  const { lang } = useContext(LangContext);
  const { utilisateur } = useAuth();
  const t = T[lang] || T.fr;

  // Back link target. Sources that link here can pass `state.from` (e.g. the
  // /my-lands dashboard) so the back button returns where the user came from
  // instead of always landing on the public /lands directory.
  const location = useLocation();
  const retourTo = location.state?.from === '/my-lands' ? '/my-lands' : '/lands';
  const retourLabel = location.state?.from === '/my-lands' ? t.retourMesTerres : t.retour;

  const [land, setLand] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErreur('');
    api
      .get(`/lands/${id}`)
      .then((res) => {
        if (!cancelled) setLand(res.data);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err.response?.status === 404) setErreur(t.introuvable);
        else setErreur(t.erreur);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, t.introuvable, t.erreur]);

  if (loading) {
    return (
      <PageShell>
        <main style={{ maxWidth: 900, margin: '0 auto', padding: '2rem 1rem', color: TEXTE_DIM }}>
          {t.chargement}
        </main>
      </PageShell>
    );
  }

  if (erreur || !land) {
    return (
      <PageShell>
        <main style={{ maxWidth: 900, margin: '0 auto', padding: '2rem 1rem' }}>
          <Link
            to={retourTo}
            style={{ color: TEXTE_DIM, textDecoration: 'none', fontSize: '0.88rem' }}
          >
            {retourLabel}
          </Link>
          <p style={{ color: '#fca5a5', marginTop: '1.5rem' }}>{erreur || t.introuvable}</p>
        </main>
      </PageShell>
    );
  }

  const listings = (land.listings || []).filter((l) => l.status === 'en_cours');
  const photoHero = land.photos?.[0];
  // Owner-only controls: show "Edit" link beside the title when the viewer's
  // user id matches the land's landowner_id. Admins also get the button —
  // they can already edit via /admin, but a one-click link from the public
  // detail page is convenient for moderation.
  const peutModifier =
    !!utilisateur &&
    (utilisateur.id === land.landowner?.id ||
      utilisateur.id === land.landowner_id ||
      utilisateur.user_type === 'admin');

  return (
    <PageShell>
      <main style={{ maxWidth: 900, margin: '0 auto', padding: '2rem 1rem 4rem' }}>
        <Link
          to={retourTo}
          style={{ color: TEXTE_DIM, textDecoration: 'none', fontSize: '0.88rem' }}
        >
          {retourLabel}
        </Link>

        {/* ── Hero / land header ── */}
        <header style={{ marginTop: '1rem', marginBottom: '2rem' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: '1rem',
              flexWrap: 'wrap',
            }}
          >
            <h1
              style={{
                fontFamily: 'Georgia, serif',
                fontSize: '2rem',
                color: TEXTE,
                margin: '0 0 0.5rem',
                lineHeight: 1.25,
                flex: 1,
                minWidth: 0,
              }}
            >
              {land.title}
            </h1>
            {peutModifier && (
              <Link
                to={`/lands/${land.id}/edit`}
                style={{
                  display: 'inline-block',
                  background: 'rgba(201,162,39,0.15)',
                  border: `1px solid ${OR}`,
                  color: OR,
                  padding: '0.5rem 1rem',
                  borderRadius: 6,
                  textDecoration: 'none',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  whiteSpace: 'nowrap',
                }}
              >
                {t.modifier}
              </Link>
            )}
          </div>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '0.6rem',
              alignItems: 'center',
              color: TEXTE_DIM,
              fontSize: '0.92rem',
            }}
          >
            <span>📍 {land.address || `${land.province} · ${land.state_region || ''}`.trim()}</span>
            {land.area_hectares != null && (
              <>
                <span>·</span>
                <span>{land.area_hectares} ha</span>
              </>
            )}
          </div>
        </header>

        {/* Photo hero (if available) */}
        {photoHero && (
          <div
            style={{
              height: 320,
              borderRadius: 12,
              background: `linear-gradient(180deg, rgba(0,0,0,0.0) 30%, rgba(0,0,0,0.5) 100%), url(${photoHero}) center/cover`,
              marginBottom: '2rem',
            }}
          />
        )}
        {!photoHero && (
          <div
            style={{
              height: 200,
              borderRadius: 12,
              background: 'linear-gradient(135deg, #1a2820 0%, #2a3a30 60%, #1a2820 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '2rem',
            }}
          >
            <span style={{ fontSize: '3rem', opacity: 0.4 }}>🌲</span>
          </div>
        )}

        {/* ── Section 1: Land info ── */}
        <section style={{ marginBottom: '3rem' }}>
          <h2
            style={{
              color: OR,
              fontSize: '1.2rem',
              marginBottom: '1rem',
              borderBottom: `1px solid ${BORDURE}`,
              paddingBottom: '0.5rem',
            }}
          >
            {t.sectionInfos}
          </h2>

          {land.description && (
            <p
              style={{
                color: TEXTE,
                lineHeight: 1.7,
                marginBottom: '1.5rem',
                whiteSpace: 'pre-wrap',
              }}
            >
              {land.description}
            </p>
          )}

          <dl
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: '1rem 1.5rem',
              margin: 0,
            }}
          >
            <div>
              <dt
                style={{
                  fontSize: '0.72rem',
                  color: TEXTE_DIM,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  marginBottom: 4,
                }}
              >
                {t.superficie}
              </dt>
              <dd style={{ margin: 0, color: TEXTE }}>{land.area_hectares ?? '—'} ha</dd>
            </div>

            <div>
              <dt
                style={{
                  fontSize: '0.72rem',
                  color: TEXTE_DIM,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  marginBottom: 4,
                }}
              >
                {t.capaciteMax}
              </dt>
              <dd style={{ margin: 0, color: TEXTE }}>
                {land.max_hunters != null ? t.chasseurs(land.max_hunters) : '—'}
              </dd>
            </div>

            {land.address && (
              <div style={{ gridColumn: 'span 2' }}>
                <dt
                  style={{
                    fontSize: '0.72rem',
                    color: TEXTE_DIM,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    marginBottom: 4,
                  }}
                >
                  {t.address}
                </dt>
                <dd style={{ margin: 0, color: TEXTE }}>{land.address}</dd>
              </div>
            )}

            {Array.isArray(land.available_species) && land.available_species.length > 0 && (
              <div style={{ gridColumn: '1 / -1' }}>
                <dt
                  style={{
                    fontSize: '0.72rem',
                    color: TEXTE_DIM,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    marginBottom: 6,
                  }}
                >
                  {t.especes}
                </dt>
                <dd style={{ margin: 0, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {land.available_species.map((e) => (
                    <span
                      key={e}
                      style={{
                        background: 'rgba(201,162,39,0.12)',
                        color: OR,
                        fontSize: '0.78rem',
                        padding: '3px 10px',
                        borderRadius: 12,
                        border: '1px solid rgba(201,162,39,0.3)',
                      }}
                    >
                      {e}
                    </span>
                  ))}
                </dd>
              </div>
            )}

            {land.rules && (
              <div style={{ gridColumn: '1 / -1' }}>
                <dt
                  style={{
                    fontSize: '0.72rem',
                    color: TEXTE_DIM,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    marginBottom: 4,
                  }}
                >
                  {t.rules}
                </dt>
                <dd style={{ margin: 0, color: TEXTE, whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                  {land.rules}
                </dd>
              </div>
            )}

            {land.landowner?.name && (
              <div>
                <dt
                  style={{
                    fontSize: '0.72rem',
                    color: TEXTE_DIM,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    marginBottom: 4,
                  }}
                >
                  {t.proprietaire}
                </dt>
                <dd style={{ margin: 0, color: TEXTE }}>{land.landowner.name}</dd>
              </div>
            )}
          </dl>
        </section>

        {/* ── Section 2: Territory map snapshot (only when one was captured
            during the wizard's map-drawing step). The owner gets a visual
            reminder of the zones they outlined; the hunter sees what's
            included/excluded on the territory before booking. ── */}
        {land.map_snapshot_base64 && (
          <section style={{ marginBottom: '3rem' }}>
            <h2
              style={{
                color: OR,
                fontSize: '1.2rem',
                marginBottom: '1rem',
                borderBottom: `1px solid ${BORDURE}`,
                paddingBottom: '0.5rem',
              }}
            >
              {t.sectionPlan}
            </h2>
            <img
              src={land.map_snapshot_base64}
              alt={t.sectionPlan}
              style={{
                width: '100%',
                maxHeight: 480,
                objectFit: 'contain',
                borderRadius: 8,
                border: `1px solid ${BORDURE}`,
                background: 'rgba(0,0,0,0.2)',
              }}
            />
          </section>
        )}

        {/* ── Section 3: Active listings ── */}
        <section>
          <h2
            style={{
              color: OR,
              fontSize: '1.2rem',
              marginBottom: '1rem',
              borderBottom: `1px solid ${BORDURE}`,
              paddingBottom: '0.5rem',
            }}
          >
            {t.sectionOffres}
            {listings.length > 0 && (
              <span
                style={{
                  marginLeft: '0.6rem',
                  color: TEXTE_DIM,
                  fontSize: '0.85rem',
                  fontWeight: 400,
                }}
              >
                ({listings.length})
              </span>
            )}
          </h2>

          {listings.length === 0 ? (
            <div
              style={{
                background: 'rgba(255,255,255,0.03)',
                border: `1px dashed ${BORDURE}`,
                borderRadius: 10,
                padding: '2.5rem 1.5rem',
                textAlign: 'center',
              }}
            >
              <div style={{ fontSize: '2.5rem', opacity: 0.35, marginBottom: '0.7rem' }}>🌿</div>
              <h3
                style={{ color: TEXTE, fontSize: '1.05rem', margin: '0 0 0.5rem', fontWeight: 600 }}
              >
                {t.aucuneOffre}
              </h3>
              <p
                style={{
                  color: TEXTE_DIM,
                  margin: '0 auto 1.5rem',
                  maxWidth: 480,
                  lineHeight: 1.55,
                  fontSize: '0.9rem',
                }}
              >
                {t.aucuneOffreDetail}
              </p>
              <Link
                to="/lands"
                style={{
                  display: 'inline-block',
                  padding: '0.55rem 1.2rem',
                  background: 'rgba(201,162,39,0.15)',
                  border: `1px solid ${OR}`,
                  color: OR,
                  borderRadius: 6,
                  textDecoration: 'none',
                  fontWeight: 600,
                  fontSize: '0.88rem',
                }}
              >
                {t.voirToutes}
              </Link>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '1rem' }}>
              {listings.map((l) => (
                <CarteOffre key={l.id} listing={l} t={t} lang={lang} />
              ))}
            </div>
          )}
        </section>
      </main>
    </PageShell>
  );
}
